import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageHero from "@/components/common/PageHero";
import PageTransition from "@/components/layout/PageTransition";
import AnimatedSection from "@/components/common/AnimatedSection";
import { pageHeroImages } from "@/config/site";
import { api } from "@/lib/api";

export default function BlogsPage() {
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const response = await api.get("/public/blogs");
        setBlogs(response.data);
      } catch {
        toast.error("Unable to load blogs right now");
      }
    };

    loadBlogs();
  }, []);

  return (
    <PageTransition testId="blogs-page">
      <PageHero
        title="Kavlekar Dental Blog"
        subtitle="Practical guidance on oral health, implants, and confidence-driven smile care."
        image={pageHeroImages.blogs}
        testId="blogs-page-hero"
      />

      <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
        <AnimatedSection testId="blogs-grid-section">
          <h2
            className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-5xl"
            data-testid="blogs-grid-title"
          >
            Your Guide to Oral Health
          </h2>

          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {blogs.map((blog, index) => (
                <motion.article
                  key={blog.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="overflow-hidden rounded-2xl border border-[#f2e6c9] bg-white shadow-[0_8px_28px_rgba(0,0,0,0.05)]"
                  data-testid={`blog-card-${blog.id}`}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={blog.image_url}
                      alt={blog.title}
                      className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                      loading="lazy"
                      data-testid={`blog-image-${blog.id}`}
                    />
                  </div>
                  <div className="p-6">
                    <p
                      className="font-[Manrope] text-xs uppercase tracking-[0.16em] text-[#8b7a55]"
                      data-testid={`blog-meta-${blog.id}`}
                    >
                      {blog.author} · {blog.published_on}
                    </p>
                    <h3
                      className="mt-2 font-[Playfair_Display] text-2xl text-[#1a1a1a]"
                      data-testid={`blog-title-${blog.id}`}
                    >
                      {blog.title}
                    </h3>
                    <p
                      className="mt-3 font-[Manrope] text-sm text-[#555555]"
                      data-testid={`blog-excerpt-${blog.id}`}
                    >
                      {blog.excerpt}
                    </p>
                    <Link
                      to={`/blogs/${blog.id}`}
                      className="mt-4 inline-flex font-[Manrope] text-sm font-semibold text-[#b08d45] underline-offset-4 transition-colors hover:text-[#8c6a2b] hover:underline"
                      data-testid={`blog-know-more-link-${blog.id}`}
                    >
                      Know More
                    </Link>
                  </div>
                </motion.article>
              ))}
          </div>
        </AnimatedSection>
      </div>
    </PageTransition>
  );
}
